import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { Input, Label, useDebouncedValue } from "@tellescope/ui";
import { formatDateTime, formatName } from "@tellescope/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface FormResponsesProps {
  contactId: string;
  formId: string;
  onResponseSelect?: (response: TellescopeModelFromName<"form_responses">) => void;
}

export const FormResponses = ({ contactId, formId, onResponseSelect }: FormResponsesProps) => {
  const { t } = useTranslation("forms");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebouncedValue(searchTerm);
  const { data: formResponses } = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {
        enduserId: contactId,
        formId,
      },
      search: debouncedSearch
        ? {
            query: debouncedSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
  });
  const { data: users } = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat().filter((user) => user.roles?.includes("admin")),
  });
  const adminIds = users?.map((user) => user.id) ?? [];

  return (
    <div className="flex flex-col gap-4 py-2 overflow-y-hidden">
      <div className="space-y-2">
        <Label className="text-sm font-medium">{t("Select Response")}</Label>
        <Input
          id="responses-search"
          placeholder="Search by responder name or date..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-3 overflow-y-auto py-1">
        {formResponses?.map((response) => (
          <div
            key={response.id}
            className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer bg-white"
            onClick={() => onResponseSelect?.(response)}
            data-testid="form-response-card"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="font-medium text-sm">
                  {response.submittedBy && adminIds.includes(response.submittedBy)
                    ? `${formatName(response)} (Admin)`
                    : formatName(response)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {response.submittedAt
                    ? formatDateTime(response.submittedAt ?? response.updatedAt)
                    : t("{{timestamp}} (unsubmitted)", {
                        timestamp: formatDateTime(response.submittedAt ?? response.updatedAt),
                      })}
                </div>
              </div>
            </div>
          </div>
        ))}

        {formResponses?.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            {debouncedSearch ? "No responses found matching your search." : "No responses yet."}
          </div>
        )}
      </div>
    </div>
  );
};
